import { useQuery } from "@tanstack/react-query";
import { KeyRound } from "lucide-react";
import { Link } from "react-router-dom";

import { apiClient } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { ProviderCredential, Uuid } from "@tilt/admin-sdk";

/**
 * Credentials tab body for Partner / Organization / Location detail
 * pages. Before push 10 each page either had no Credentials tab
 * (Partner, Organization) or one that filtered strictly by the scope's
 * own id (Location) - which hid inherited rows and made an operator
 * with an org-scoped credential think "no credentials here" when
 * really the credentials were one level up.
 *
 * This panel fetches every credential visible under the partner root
 * in one call, then groups by the credential's actual scope so the
 * operator sees both "attached here" and "inherited from …" blocks
 * explicitly. Full CRUD still lives on /provider-credentials; rows here link
 * through for rotate / reveal / delete.
 */
export interface ScopedCredentialsPanelProps {
  partnerId: Uuid;
  partnerName: string;
  organizationId?: Uuid | undefined;
  organizationName?: string | undefined;
  locationId?: Uuid | undefined;
  locationName?: string | undefined;
}

export function ScopedCredentialsPanel(props: ScopedCredentialsPanelProps): JSX.Element {
  // One fetch per partner subtree. Backend's listProviderCredentials
  // filters by partner_id across all scope levels, so this single
  // query populates the "attached here" + "inherited from …" groups
  // without a separate round-trip per ancestor. limit=200 keeps the
  // common case (a partner with <=200 credentials) single-request;
  // the paginated Credentials page handles the long-tail partner.
  const query = useQuery({
    queryKey: ["provider-credentials", "scoped-panel", props.partnerId],
    queryFn: () =>
      apiClient.listProviderCredentials({
        partner_id: props.partnerId,
        limit: 200,
        include_deleted: false,
      }),
    retry: false,
  });

  if (query.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading credentials…</p>;
  }
  if (query.isError) {
    const msg = query.error instanceof Error ? query.error.message : "Failed to load credentials.";
    return <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{msg}</div>;
  }

  const all = query.data?.credentials ?? [];

  // Classify each credential by its own scope (not the caller's).
  // The sections we render depend on *this* panel's scope: a partner
  // panel shows partner-scoped creds only (org/location inheritance
  // points downward, not upward); an org panel adds its own org
  // section + the partner inheritance block; a location panel shows
  // everything.
  const partnerScoped = all.filter((c) => !c.organization_id && !c.location_id);
  const orgScoped = (scope: Uuid | undefined): ProviderCredential[] =>
    scope ? all.filter((c) => c.organization_id === scope && !c.location_id) : [];
  const locScoped = (scope: Uuid | undefined): ProviderCredential[] =>
    scope ? all.filter((c) => c.location_id === scope) : [];

  // Also surface credentials at sibling scopes that this viewer does
  // NOT inherit from - rendered under a muted "Other scopes in this
  // partner" header so the operator can see the full picture without
  // confusing them about inheritance.
  const inScope = new Set<string>();
  partnerScoped.forEach((c) => inScope.add(c.id));
  orgScoped(props.organizationId).forEach((c) => inScope.add(c.id));
  locScoped(props.locationId).forEach((c) => inScope.add(c.id));
  const otherScoped = all.filter((c) => !inScope.has(c.id));

  const sections: Array<{
    key: string;
    title: string;
    subtitle: string;
    creds: ProviderCredential[];
    ownScope: boolean;
  }> = [];

  // Own-scope section - what the admin can treat as "attached here".
  if (props.locationId) {
    sections.push({
      key: "location",
      title: `Attached to this location${props.locationName ? ` (${props.locationName})` : ""}`,
      subtitle: "These credentials only apply here.",
      creds: locScoped(props.locationId),
      ownScope: true,
    });
    // Inherited from org + partner.
    if (props.organizationId) {
      sections.push({
        key: "organization",
        title: `Inherited from organization${
          props.organizationName ? ` (${props.organizationName})` : ""
        }`,
        subtitle: "Shared across every location in this organization.",
        creds: orgScoped(props.organizationId),
        ownScope: false,
      });
    }
    sections.push({
      key: "partner",
      title: `Inherited from partner (${props.partnerName})`,
      subtitle: "Shared across every organization + location under this partner.",
      creds: partnerScoped,
      ownScope: false,
    });
  } else if (props.organizationId) {
    sections.push({
      key: "organization",
      title: `Attached to this organization${
        props.organizationName ? ` (${props.organizationName})` : ""
      }`,
      subtitle: "Shared across every location in this organization.",
      creds: orgScoped(props.organizationId),
      ownScope: true,
    });
    sections.push({
      key: "partner",
      title: `Inherited from partner (${props.partnerName})`,
      subtitle: "Shared across every organization under this partner.",
      creds: partnerScoped,
      ownScope: false,
    });
  } else {
    sections.push({
      key: "partner",
      title: `Attached to this partner (${props.partnerName})`,
      subtitle: "Shared across every organization + location under this partner.",
      creds: partnerScoped,
      ownScope: true,
    });
  }

  const totalShown = sections.reduce((n, s) => n + s.creds.length, 0);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4">
        <div className="mb-2 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold">Effective credentials</h2>
            <p className="text-xs text-muted-foreground">
              Every credential available at this scope, grouped by where it lives. Full CRUD
              (create, rotate, reveal, delete) lives on the{" "}
              <Link to="/provider-credentials" className="text-primary hover:underline">
                Credentials page
              </Link>
              .
            </p>
          </div>
          {totalShown > 0 && (
            <span className="text-xs text-muted-foreground">
              {totalShown} credential{totalShown === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>

      {sections.map(({ key, ...s }) => (
        <SectionBlock key={key} {...s} />
      ))}

      {otherScoped.length > 0 && (
        <SectionBlock
          title="Other scopes in this partner"
          subtitle="Credentials elsewhere under the same partner - shown for full visibility; they don't apply to this scope."
          creds={otherScoped}
          ownScope={false}
          muted
        />
      )}
    </div>
  );
}

function SectionBlock(props: {
  title: string;
  subtitle: string;
  creds: ProviderCredential[];
  ownScope: boolean;
  muted?: boolean;
}): JSX.Element {
  return (
    <section className={cn("rounded-lg border bg-card", props.muted && "opacity-70")}>
      <div className="border-b px-4 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {props.title}
        </h3>
        <p className="text-[11px] text-muted-foreground">{props.subtitle}</p>
      </div>
      {props.creds.length === 0 ? (
        <p className="px-4 py-3 text-xs text-muted-foreground">None.</p>
      ) : (
        <ul className="divide-y">
          {props.creds.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                <KeyRound className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="truncate font-medium">{c.label}</div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    <span className="font-mono">{c.provider}</span>
                    {" · "}
                    <span>{c.environment}</span>
                    {c.last_rotated_at && (
                      <>
                        {" · rotated "}
                        {c.last_rotated_at.slice(0, 10)}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <Link
                to={`/provider-credentials?highlight=${c.id}`}
                className="flex-shrink-0 text-xs text-primary hover:underline"
              >
                Manage →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
